import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Calendar, Share2, User, Tag } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import { toast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";


const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find((post) => post.slug === slug);
  
  // Sort blog posts by date in descending order (newest first)
  const sortedBlogPosts = [...blogPosts].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  
  useEffect(() => {
    if (!post) {
      navigate("/blog");
    }
    
    window.scrollTo(0, 0);
  }, [post, navigate]);
  
  if (!post) return null;

  // Normalize leading whitespace so embedded HTML in markdown isn't treated as code blocks
  const normalizedContent = post.content.replace(/^\s+</gm, "<");

  // Build a description fallback if excerpt is missing/empty
  const buildDescription = () => {
    if (post.excerpt && post.excerpt.trim().length > 0) return post.excerpt.trim();
    // crude markdown strip for fallback snippet
    const text = normalizedContent
      .replace(/<[^>]*>/g, " ")
      .replace(/[`*_>#-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.slice(0, 160) || "Read this YouAux blog post.";
  };

  const metaDescription = buildDescription();
  
  // Get previous and next post for navigation based on chronological order
  const currentIndex = sortedBlogPosts.findIndex((p) => p.slug === slug);
  const prevPost = currentIndex > 0 ? sortedBlogPosts[currentIndex - 1] : null; // Newer post
  const nextPost = currentIndex < sortedBlogPosts.length - 1 ? sortedBlogPosts[currentIndex + 1] : null; // Older post
  
  // FAQs to render (fallback if not provided)
  const faqsToRender = (post.faqs && post.faqs.length > 0
    ? post.faqs
    : [
        {
          question: `What is ${post.title}?`,
          answer: post.excerpt
        },
        {
          question: "How can YouAux help?",
          answer: "YouAux provides distribution, rights management, analytics, and payouts to support independent artists."
        }
      ]
  );

  return (
    <>
    <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://youaux.com/blog/${post.slug}`} />
        {/* FAQPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: (post.faqs && post.faqs.length > 0
              ? post.faqs
              : [
                  {
                    question: `What is ${post.title}?`,
                    answer: post.excerpt
                  },
                  {
                    question: 'How can YouAux help?',
                    answer: 'YouAux provides distribution, rights management, analytics, and payouts to support independent artists.'
                  }
                ]
            ).map((qa) => ({
              '@type': 'Question',
              name: qa.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: qa.answer
              }
            }))
          })}
        </script>
      </Helmet>
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <div className="w-full bg-ophira-dark pt-20 pb-10 relative overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <Link to="/blog" className="flex items-center text-ophira-yellow hover:underline mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to all articles
            </Link>
            <div className="max-w-4xl">
              <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">{post.title}</h1>
              <div className="flex flex-wrap items-center gap-6 mb-8">
                <div className="flex items-center text-ophira-lightgray">
                  <Calendar className="h-4 w-4 mr-2 text-ophira-yellow" />
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center text-ophira-lightgray">
                  <User className="h-4 w-4 mr-2 text-ophira-yellow" />
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center text-ophira-lightgray">
                  <Tag className="h-4 w-4 mr-2 text-ophira-yellow" />
                  <span>{post.category}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-noise-pattern opacity-5 pointer-events-none"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
        </div>

        {/* Content */}
        <div className="bg-ophira-dark pb-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-8">
                {/* Featured Image - Left positioned above content */}
                <div className="relative mb-12 rounded-2xl overflow-hidden shadow-2xl bg-black">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className={`w-full rounded-2xl ${
                      post.id === 8 
                        ? 'h-auto object-cover max-h-[700px]' 
                        : post.id === 9 
                          ? 'h-auto object max-h-[700px]' 
                          : post.id === 10
                            ? 'h-auto object max-h-[650px]'
                            : post.id === 11
                              ? 'h-auto object max-h-[500px]'
                              : post.id === 12
                                ? 'h-auto w-auto object-contain max-h-[450px] bg-[#121212]'
                                : post.id === 13
                                  ? 'h-auto object max-h-[500px]'
                                  : post.id === 14
                                    ? 'h-auto object max-h-[500px]'
                                  : post.id === 15
                                    ? 'h-auto object max-h-[500px]'
                                  : post.id === 16
                                    ? 'h-auto object max-h-[500px]'
                                  : post.id === 17
                                    ? 'h-auto object max-h-[500px]'
                            : 'h-64 md:h-80 object-cover'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-ophira-yellow text-black px-3 py-1 rounded-full text-sm font-medium">{post.category}</span>
                      <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">{post.author}</span>
                      <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">{post.date}</span>
                    </div>
                  </div>
                </div>
                <div className="prose prose-lg prose-invert max-w-none blog-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    skipHtml={false}
                  >
                    {normalizedContent}
                  </ReactMarkdown>
                </div>
                
                {/* FAQ Section */}
                <div className="mt-12 pt-8 border-t border-ophira-darkgray/50">
                  <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
                  <div className="space-y-4">
                    {faqsToRender.map((qa, idx) => (
                      <details key={idx} className="group bg-ophira-darkgray/40 rounded-xl p-4">
                        <summary className="cursor-pointer font-medium text-white list-none flex items-center justify-between">
                          <span>{qa.question}</span>
                          <span className="ml-4 text-ophira-yellow transition-transform group-open:rotate-45">+</span>
                        </summary>
                        <div className="mt-3 text-ophira-lightgray">
                          {qa.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>

                {/* Share Section */}
                <div className="mt-12 pt-8 border-t border-ophira-darkgray/50">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <span className="text-ophira-lightgray mr-4">Share this article:</span>
                      <div className="inline-flex gap-2">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="rounded-full bg-ophira-darkgray/40 border-ophira-darkgray"
                          onClick={() => {
                            const url = window.location.href;
                            navigator.clipboard.writeText(url);
                            // Show toast notification instead of alert
                            toast({
                              title: "Link copied",
                              description: "Article link copied to clipboard",
                              duration: 3000,
                            });
                          }}
                        >
                          <Share2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Link to="/contact">
                        <Button className="ophira-button rounded-2xl">Contact Us</Button>
                      </Link>
                    </div>
                  </div>
                </div>
                
                {/* Post Navigation */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {prevPost && (
                    <Link 
                      to={`/blog/${prevPost.slug}`} 
                      className="bg-ophira-darkgray/40 p-6 group rounded-2xl"
                    >
                      <div className="flex items-center text-ophira-yellow mb-2">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        <span className="text-sm uppercase">Newer Post</span>
                      </div>
                      <h4 className="font-medium group-hover:text-ophira-yellow transition-colors">
                        {prevPost.title}
                      </h4>
                    </Link>
                  )}
                  
                  {nextPost && (
                    <Link 
                      to={`/blog/${nextPost.slug}`} 
                      className="bg-ophira-darkgray/40 p-6 text-right group rounded-2xl"
                    >
                      <div className="flex items-center justify-end text-ophira-yellow mb-2">
                        <span className="text-sm uppercase">Older Post</span>
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </div>
                      <h4 className="font-medium group-hover:text-ophira-yellow transition-colors">
                        {nextPost.title}
                      </h4>
                    </Link>
                  )}
                </div>
              </div>
              
              {/* Sidebar */}
              <div className="lg:col-span-4">
                <div className="bg-ophira-darkgray/40 p-6 mb-8 rounded-2xl">
                  <h3 className="text-xl font-bold mb-6 border-b border-ophira-darkgray/50 pb-4">
                    Recent Articles
                  </h3>
                  <div className="space-y-6">
                    {sortedBlogPosts.slice(0, 3).map((item) => (
                      <Link 
                        key={item.id} 
                        to={`/blog/${item.slug}`}
                        className="flex gap-4 group"
                      >
                        <img 
                          src={item.image} 
                          alt={item.title} 
                          className="w-20 h-20 object-cover flex-shrink-0"
                        />
                        <div>
                          <h4 className="font-medium text-white group-hover:text-ophira-yellow transition-colors">
                            {item.title}
                          </h4>
                          <span className="text-sm text-ophira-lightgray">
                            {item.date}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
                
                <div className="bg-ophira-darkgray/40 p-6 rounded-2xl">
                  <h3 className="text-xl font-bold mb-6 border-b border-ophira-darkgray/50 pb-4">
                    Categories
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-ophira-lightgray hover:text-white transition-colors">
                        Music Distribution
                      </span>
                      <span className="text-sm text-ophira-yellow">12</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-ophira-lightgray hover:text-white transition-colors">
                        Marketing
                      </span>
                      <span className="text-sm text-ophira-yellow">8</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-ophira-lightgray hover:text-white transition-colors">
                        Music Production
                      </span>
                      <span className="text-sm text-ophira-yellow">5</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-ophira-lightgray hover:text-white transition-colors">
                        Industry News
                      </span>
                      <span className="text-sm text-ophira-yellow">3</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default BlogPost;
